import get from 'lodash/get';
import PropTypes from 'prop-types';

import { useTransl } from 'shared/lib/i18n';

import ModalHandlerComponent from '../Modal/ModalHandlerComponent';
import SortableTableComponent from '../Table/SortableTableComponent';
import GiveAccessComponent from './GiveAccessComponent';

import { customSorting, toSentenceCase } from '../../utils';
import { getFullName } from '../Person/utils';

import { TABLE_ID } from '../Table/constants';
import { GIVE_ACCESS } from './constants';

import { StyledCallCenterTableContainer } from './styledComponents';

const getRoles = (roles) => {
  if (!roles?.length) return null;
  return [
    ...new Set(roles.map(({ roleName }) => toSentenceCase(roleName))),
  ].join(', ');
};

const getUnits = (roles) => {
  if (!roles?.length) return null;
  return [...new Set(roles.map(({ propertyName }) => propertyName))].join(', ');
};

const CallCenterResidentComponent = ({
  persons,
  modal,
  modalProps,
  onModalShow,
  onOpenAccessPointForResident,
  requirePhotoForVisitorLockout,
}) => {
  const { t } = useTransl();

  const columns = [
    {
      Header: t('title.name'),
      id: 'name',
      headerClassName: 'sort',
      minWidth: 200,
      accessor: (person) => getFullName(person),
      filter: true,
      sortMethod: (a, b) =>
        customSorting(
          get(a, 'props.person.firstName', null),
          get(b, 'props.person.firstName', null),
        ),
      dataKey: (person) => getFullName(person),
    },
    {
      Header: t('title.unit'),
      id: 'unitName',
      accessor: ({ roles }) => getUnits(roles),
      headerClassName: 'sort',
      dataKey: ({ roles }) => getUnits(roles),
      filter: true,
    },
    {
      Header: t('title.role'),
      id: 'role',
      accessor: ({ roles }) => getRoles(roles),
      headerClassName: 'sort',
      dataKey: ({ roles }) => getRoles(roles),
      filter: true,
    },
  ];

  const onRowClick = (rowInfo) => onModalShow(GIVE_ACCESS, rowInfo?.original);

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  const isStaffSelected = modal?.data?.roles?.find(({ isStaff }) => isStaff);
  const disableIfNoPhoto = requirePhotoForVisitorLockout && !isStaffSelected;

  return (
    <section>
      <StyledCallCenterTableContainer>
        <SortableTableComponent
          tableId={TABLE_ID.ccResidents}
          title={t('callCenter.residentsOrStaff')}
          columns={columns}
          sortKey="name" // used instead of defaultSort when isVirtualizedTable
          entries={persons}
          tableDataType={null}
          onRowClick={onRowClick}
          rowStyles={rowStyles}
          isVirtualizedTable
        />
      </StyledCallCenterTableContainer>
      {modal?.type === GIVE_ACCESS && (
        <ModalHandlerComponent {...modalProps}>
          <GiveAccessComponent
            buttonLabel={t('callCenter.giveAccess')}
            disabledButtonLabel={t('callCenter.photoRequired')}
            onOpenAccessPoint={onOpenAccessPointForResident}
            disableIfNoPhoto={disableIfNoPhoto}
            {...modal?.data}
          />
        </ModalHandlerComponent>
      )}
    </section>
  );
};

CallCenterResidentComponent.propTypes = {
  persons: PropTypes.instanceOf(Array).isRequired,
  modal: PropTypes.instanceOf(Object).isRequired,
  onModalShow: PropTypes.func.isRequired,
  onOpenAccessPointForResident: PropTypes.func.isRequired,
  modalProps: PropTypes.instanceOf(Object).isRequired,
  requirePhotoForVisitorLockout: PropTypes.bool,
};

export default CallCenterResidentComponent;
